import { api, getAccessToken } from "@/lib/api/client";

export interface Shift {
  id: string;
  code: string;
  name: string;
  start_time: string;
  end_time: string;
  ends_next_day: boolean;
  no_consecutive_days: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface Position {
  id: string;
  name: string;
  description: string | null;
  /** What the job is, in full. New joiners sign the one for their position. */
  job_description: string | null;
  /** Goes up each time the text changes; a signed copy names its version. */
  job_description_version: number;
  job_description_updated_at: string | null;
  /** Holding a manager's position is what grants manager access. */
  is_manager: boolean;
  /** Whether the rota decides this job's days off rather than the profile. */
  rotates_days_off: boolean;
  /** The hours this job is normally due, for staff the rota never draws. */
  start_time: string | null;
  end_time: string | null;
  is_active: boolean;
  sort_order: number;
  headcount: number;
  /** Active staff holding it at all, as a main or a second position. */
  holders: number;
}

/** What sending a position's job description to its holders did. */
export interface JobDescriptionSendResult {
  people: number;
  /** Unsigned copies of an earlier version withdrawn. */
  withdrawn: number;
  /** Copies of the current version sent. */
  sent: number;
}

export interface StaffingRequirement {
  id: string;
  branch_id: string;
  shift_id: string;
  position_id: string;
  weekday: number | null;
  headcount: number;
}

export interface RequirementInput {
  shift_id: string;
  position_id: string;
  weekday?: number | null;
  headcount: number;
}

export interface SlotStaff {
  assignment_id: string;
  user_id: string;
  name: string;
  employee_number: string | null;
  position_id: string | null;
  position_name: string | null;
  covering_from: string | null;
  on_rest_day: boolean;
  note: string | null;
  /** Holds a manager's position: on a counter, the head teller. */
  is_head: boolean;
  /** Holds a position that takes its days off from the rota: counter staff. */
  on_counter: boolean;
}

export interface SlotRequirement {
  position_id: string;
  position_name: string;
  required: number;
  filled: number;
  missing: number;
}

export interface ShiftSlot {
  shift_id: string;
  shift_code: string;
  shift_name: string;
  start_time: string;
  end_time: string;
  ends_next_day: boolean;
  staff: SlotStaff[];
  requirements: SlotRequirement[];
  required_total: number;
  missing_total: number;
  short: boolean;
}

export interface RotaBranch {
  branch_id: string;
  branch_code: string;
  branch_name: string;
  shifts: ShiftSlot[];
}

export interface RotaAway {
  user_id: string;
  name: string;
  employee_number: string | null;
  reason: string;
}

export interface RotaAvailable {
  user_id: string;
  name: string;
  employee_number: string | null;
  position_id: string | null;
  position_name: string | null;
  home_branch_id: string | null;
  blocked_shift_ids: string[];
}

export interface RotaDay {
  date: string;
  branches: RotaBranch[];
  on_leave: RotaAway[];
  resting: RotaAway[];
  available: RotaAvailable[];
  missing_total: number;
}

export interface MyShift {
  assignment_id: string;
  shift_name: string;
  start_time: string;
  end_time: string;
  ends_next_day: boolean;
  branch_id: string;
  branch_name: string;
  note: string | null;
}

export interface MyRotaDay {
  date: string;
  shift: MyShift | null;
  on_leave: string | null;
  resting: boolean;
  public_holiday: string | null;
}

export interface AssignmentRow {
  id: string;
  user_id: string;
  work_date: string;
  shift_id: string;
  branch_id: string;
  note: string | null;
}

/** What the guideline could not fill on one day. */
export interface DrawShort {
  day: string;
  branch_name: string;
  shift_name: string;
  what: string;
}

/** Somebody drawn at another branch because their own needed nobody that day. */
export interface DrawMove {
  day: string;
  name: string;
  from_branch: string;
  to_branch: string;
}

export interface DrawResult {
  assigned: number;
  cleared: number;
  people: number;
  short: DrawShort[];
  moved: DrawMove[];
}

export const rotaKeys = {
  all: ["rota"] as const,
  shifts: () => [...rotaKeys.all, "shifts"] as const,
  positions: (includeInactive = false) =>
    [...rotaKeys.all, "positions", includeInactive] as const,
  requirements: (branchId?: string) => [...rotaKeys.all, "requirements", branchId ?? "*"] as const,
  week: (from: string, to: string, branchId?: string) =>
    [...rotaKeys.all, "week", from, to, branchId ?? "*"] as const,
  mine: (from: string, to: string) => [...rotaKeys.all, "mine", from, to] as const,
  forecast: (weeks: number) => [...rotaKeys.all, "forecast", weeks] as const,
};

export const rotaApi = {
  shifts: () => api.get<Shift[]>("/shifts"),

  forecast: (weeks: number) =>
    api.get<BranchForecast[]>("/staffing-forecast", { query: { weeks } }),

  positions: (includeInactive = false) =>
    api.get<Position[]>("/positions", {
      query: includeInactive ? { include_inactive: true } : undefined,
    }),

  requirements: (branchId?: string) =>
    api.get<StaffingRequirement[]>("/staffing-requirements", {
      query: branchId ? { branch_id: branchId } : undefined,
    }),

  setRequirements: (branchId: string, requirements: RequirementInput[]) =>
    api.put<StaffingRequirement[]>(`/branches/${branchId}/staffing-requirements`, {
      requirements,
    }),

  week: (from: string, to: string, branchId?: string) =>
    api.get<RotaDay[]>("/rota", {
      query: branchId ? { from, to, branch_id: branchId } : { from, to },
    }),

  mine: (from: string, to: string) => api.get<MyRotaDay[]>("/me/rota", { query: { from, to } }),

  assign: (payload: {
    user_id: string;
    work_date: string;
    shift_id: string;
    branch_id: string;
    note?: string;
  }) => api.post<AssignmentRow>("/rota/shifts", payload),

  move: (
    assignmentId: string,
    payload: {
      user_id?: string;
      work_date?: string;
      shift_id?: string;
      branch_id?: string;
      note?: string;
    },
  ) => api.patch<AssignmentRow>(`/rota/shifts/${assignmentId}`, payload),

  unassign: (assignmentId: string) => api.delete<{ message: string }>(`/rota/shifts/${assignmentId}`),

  /** Fill a span of days to the counter guideline. */
  draw: (payload: { from: string; to: string; branch_id?: string; replace: boolean }) =>
    api.post<DrawResult>("/rota/draw", payload),

  /** Replace what a branch asks for with the counter guideline. */
  guidelineRequirements: (branchId: string) =>
    api.post<StaffingRequirement[]>(`/branches/${branchId}/staffing-requirements/guideline`),

  createPosition: (payload: {
    name: string;
    description?: string;
    job_description?: string;
    is_manager?: boolean;
  }) => api.post<Position>("/positions", payload),

  updatePosition: (
    id: string,
    payload: {
      name?: string;
      description?: string;
      job_description?: string;
      is_manager?: boolean;
      is_active?: boolean;
    },
  ) => api.patch<Position>(`/positions/${id}`, payload),

  deletePosition: (id: string) => api.delete<{ message: string }>(`/positions/${id}`),

  /** Send the current job description to everybody holding the position. */
  sendJobDescription: (id: string) =>
    api.post<JobDescriptionSendResult>(`/positions/${id}/job-description/send`),

  updateShift: (
    id: string,
    payload: {
      name?: string;
      start_time?: string;
      end_time?: string;
      ends_next_day?: boolean;
      no_consecutive_days?: boolean;
    },
  ) => api.patch<Shift>(`/shifts/${id}`, payload),
};

/** One branch, one position, one week of the forecast. */
export interface WeekShortfall {
  week_start: string;
  required: number;
  available: number;
  short: number;
}

export interface PositionForecast {
  position_id: string;
  position_name: string;
  /** The busiest day of a normal week: what the branch must be able to staff. */
  required: number;
  weeks: WeekShortfall[];
  worst: number;
  first_short_week: string | null;
  /** Why the count falls. */
  leaving: string[];
  away: string[];
}

export interface BranchForecast {
  branch_id: string;
  branch_code: string;
  branch_name: string;
  worst: number;
  positions: PositionForecast[];
}

/**
 * The job description drawn as the PDF people sign, from text not yet saved.
 *
 * Fetched with the bearer token and returned as an object URL for the caller
 * to show and later revoke, because the drawing sits behind the same
 * authorisation as everything else and a bare link carries no token.
 */
export async function previewJobDescription(positionId: string, text: string): Promise<string> {
  const response = await fetch(`/api/v1/positions/${positionId}/job-description/preview`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getAccessToken() ?? ""}`,
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ text }),
  });
  if (!response.ok) throw new Error("The preview could not be drawn.");
  return URL.createObjectURL(await response.blob());
}
